import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TextField } from '@elementary/common-ui/components';
import type { IncidentV2Summary } from '../../types';
import useIncidentSummary from '../../hooks/useIncidentSummary';

const PLACEHOLDER = 'Add a summary...';
const MAX_ROWS = 10;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    padding: 0;
    background-color: transparent;
    border: none !important;

    &:hover,
    &.Mui-focused {
      border: none !important;
    }
  }

  .MuiOutlinedInput-root {
    border: none !important;

    .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }

    &:hover .MuiOutlinedInput-notchedOutline,
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
  }

  .MuiInputBase-input {
    padding: 0;
    font-size: 14px;
    line-height: 1.5;

    &::placeholder {
      color: ${({ theme }) => theme.palette.common.slate};
      opacity: 1;
    }
  }
`;

type Props = {
  incidentId: string;
  summaries: IncidentV2Summary[];
  isReadOnly?: boolean;
};

const IncidentSummary = ({
  incidentId,
  summaries,
  isReadOnly = false,
}: Props) => {
  const { userSummary, saveSummary, removeSummary, canEdit } =
    useIncidentSummary({
      incidentId,
      summaries,
    });

  const [value, setValue] = useState(userSummary?.content || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(userSummary?.content || '');
  }, [userSummary?.content]);

  if (isReadOnly && !userSummary?.content?.trim()) {
    return null;
  }

  const shouldBeReadOnly = isReadOnly || !canEdit;

  const handleBlur = () => {
    if (shouldBeReadOnly) return;
    const trimmedValue = value.trim();
    const originalContent = userSummary?.content || '';

    if (trimmedValue === originalContent) {
      return;
    }

    if (trimmedValue) {
      saveSummary(trimmedValue);
    } else if (userSummary) {
      removeSummary();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setValue(userSummary?.content || '');
      inputRef.current?.blur();
    }
  };

  return (
    <StyledTextField
      inputRef={inputRef}
      fullWidth
      multiline
      maxRows={MAX_ROWS}
      value={value}
      placeholder={PLACEHOLDER}
      onChange={shouldBeReadOnly ? undefined : handleChange}
      onBlur={shouldBeReadOnly ? undefined : handleBlur}
      onKeyDown={shouldBeReadOnly ? undefined : handleKeyDown}
      readOnly={shouldBeReadOnly}
    />
  );
};

export default IncidentSummary;
